import { z } from 'zod';

import type { AuthorKind, Country, DgQuestionType, PipelineConstraintKey } from './artifacts.ts';
import { type RichText, richTextSchema } from './brief-counts.ts';
import type {
  EvidenceScenario,
  EvidenceSource,
  EvidenceUnavailableReason,
} from './brief-evidence.ts';
import { canonicalJson } from './canonical-json.ts';
import type { FollowUpRecord } from './brief-follow-ups.ts';
import type { BriefChangeNotes } from './brief-framing.ts';
import type { BriefHistoryEntry, BriefRevision } from './brief-history.ts';
import type { StudyState } from './lifecycle.ts';
import { CONFIDENCE_TIERS } from './quality.ts';
import type { ArtifactVersionReason } from './version-reasons.ts';

/**
 * The Brief: one page a study's owner can hand to a stakeholder, written once its answers have been
 * read. Six blocks, always in this order, each stored under its own key so a later edit can be held
 * to the blocks it may touch.
 *
 * Every word the synthesis wrote is `RichText`, so each number in it is a count bound to what it
 * counts. Everything else — the research questions, every option's wording, every quote, the Method
 * and the Limitations — is copied in from the accepted inputs and the evidence when the Brief is
 * composed, so a version reads the same whatever happens to the study after it.
 *
 * Nothing in this module may read a value from `artifacts.ts`, which registers this schema: the
 * shapes it shares with the guide and the audience are restated here and pinned to theirs by type.
 */
export const BRIEF_BLOCKS = [
  'verdict',
  'summary',
  'recommendation',
  'evidence',
  'method',
  'appendix',
] as const;
export type BriefBlock = (typeof BRIEF_BLOCKS)[number];

/** The most quotes one evidence card shows. */
export const BRIEF_CARD_QUOTE_LIMIT = 3;

/** The most themes a themes visual shows before its "+K more"; the rest are behind it. */
export const BRIEF_THEMES_SHOWN = 8;

/** The share of a research question's coded people a theme must reach to be kept (`briefThemeFloor`). */
export const BRIEF_THEME_MIN_SHARE = 0.05;

/** The fewest people a kept theme is raised by, however few were coded (`briefThemeFloor`). */
export const BRIEF_THEME_MIN_PEOPLE = 2;

/** Theme labels that hold what coding could not place, matched trimmed and in any case: ranked last. */
export const BRIEF_CATCH_ALL_THEME_LABELS: readonly string[] = ['other', 'uninformative answer'];

/** The most items the summary and the recommendations each hold. */
export const MAX_BRIEF_LIST_ITEMS = 6;

/** The longest headline the synthesis may write, counted in its own words; a longer one is cut. */
export const MAX_BRIEF_HEADLINE_CHARS = 200;

/** The longest passage the synthesis may write anywhere else, counted the same way. */
export const MAX_BRIEF_PASSAGE_CHARS = 1200;

/**
 * How long a synthesis may take before it is abandoned and settles `unavailable: timed_out`: about
 * 2.6 times the slowest the example set has measured (46.5 s, for eight research questions). No
 * screen waits on it, so it is not cut to a step's wait. A placeholder.
 */
export const BRIEF_SYNTHESIS_TIMEOUT_MS = 120_000;

/**
 * The least of a synthesis's time a second call is made with, after a first whose answer broke a
 * rule: above the slowest call the example set has measured, so a second call given less would
 * mostly be paid for and abandoned. A placeholder.
 */
export const BRIEF_SYNTHESIS_RETRY_MIN_MS = 50_000;

/** The most quotes per served question a synthesis is shown; the Appendix still holds every one. */
export const BRIEF_PROMPT_QUOTES_PER_QUESTION = 40;

/** The longest text a Brief stores in one field. A storage bound. */
export const MAX_BRIEF_STORED_TEXT = 4000;
const MAX_STORED_ITEMS = 2000;

/** The most evidence cards, and the most Not settled entries, a Brief holds. A storage bound. */
export const MAX_BRIEF_ENTRIES = MAX_STORED_ITEMS;

const storedText = z.string().min(1).max(MAX_BRIEF_STORED_TEXT);
const count = z.int().nonnegative();

/**
 * Lists this module shares with the guide, the audience and the evidence, restated because their
 * modules read `artifacts.ts`; the types hold each to a subset of its original, and a unit test to
 * all of it.
 */
const QUESTION_TYPES = [
  'open_response',
  'single_choice',
  'multiple_choice',
] as const satisfies readonly DgQuestionType[];
const COUNTRIES = ['UK', 'US'] as const satisfies readonly Country[];
const PIPELINE_CONSTRAINT_KEYS = [
  'language',
  'countries',
  'simulated_participants',
] as const satisfies readonly PipelineConstraintKey[];

const FIXTURE_SCENARIOS = [
  'known',
  'conflicting',
  'empty_rq',
  'numerals',
  'themes',
] as const satisfies readonly EvidenceScenario[];
const EVIDENCE_SOURCES = ['fixture', 'remesh'] as const satisfies readonly EvidenceSource[];

const itemId = (prefix: string) => z.string().regex(new RegExp(`^${prefix}[1-9]\\d*$`));

const listItem = (prefix: string) => z.strictObject({ id: itemId(prefix), text: richTextSchema });

/** A summary bullet, and whether the researcher left it showing. */
const summaryItem = z.strictObject({
  id: itemId('S'),
  text: richTextSchema,
  visible: z.boolean(),
});

/** A passage's counts, totals and option wordings, in order: everything in it but its words. */
export const richTextChips = (text: RichText): RichText => text.filter((each) => each.t !== 'text');

/** Two passages with the same words and the same counts, totals and option wordings, values and all. */
export const sameRichText = (left: RichText, right: RichText): boolean =>
  canonicalJson(left) === canonicalJson(right);

/** Whether `order` names every one of `ids` exactly once, in any order. */
export const isPermutationOf = (order: readonly string[], ids: readonly string[]): boolean =>
  order.length === ids.length &&
  new Set(order).size === order.length &&
  order.every((id) => ids.includes(id));

const briefQuoteSchema = z.strictObject({
  quote_id: z.string().min(1),
  participant: z.string().min(1),
  dg_question_id: z.uuid(),
  text: z.string().min(1),
});

const pollVisualSchema = z.strictObject({
  kind: z.literal('poll'),
  dg_question_id: z.uuid(),
  question_text: storedText,
  multi_select: z.boolean(),
  respondents: count,
  options: z.array(z.strictObject({ option_id: z.uuid(), text: storedText, count })).min(1),
});

const themesVisualSchema = z.strictObject({
  kind: z.literal('themes'),
  /** Each theme counts a person once: the visual carries the footnote that says so. */
  counting: z.literal('once_per_person'),
  coded_participants: count,
  /** The kept themes in the order `briefThemesView` ranks them; the first `BRIEF_THEMES_SHOWN` show. */
  themes: z.array(
    z.strictObject({
      theme_id: z.string().min(1),
      label: storedText,
      participants: count,
      /** The guide question a theme was coded on, on a theme whose label another kept theme repeats. */
      source: storedText.optional(),
    }),
  ),
  /**
   * The floor the themes were held to: a theme raised by fewer people was left out. Absent on a
   * Brief composed before the visual carried it, which then says nothing of a floor.
   */
  floor: count.optional(),
  /** How many of the research question's themes fell below `floor`; absent with it. */
  left_out: count.optional(),
});

/** Why something is not settled: the guide never asked it, nobody answered, or the answers don't. */
export const NOT_SETTLED_KINDS = ['not_asked', 'no_answers', 'unsettled'] as const;
export type NotSettledKind = (typeof NOT_SETTLED_KINDS)[number];

const notSettledSchema = z
  .strictObject({
    id: z.string().regex(/^N[1-9]\d*$/),
    rq_id: z.uuid().nullable(),
    kind: z.enum(NOT_SETTLED_KINDS),
    /** What is not settled, in the synthesis's words; none where the kind's own sentence says it. */
    what: richTextSchema.nullable(),
    follow_up: richTextSchema,
  })
  .superRefine((entry, context) => {
    if ((entry.kind === 'unsettled') !== (entry.what !== null)) {
      context.addIssue({
        code: 'custom',
        path: ['what'],
        message: 'only an unsettled entry says what is unsettled',
      });
    }
    if (entry.kind !== 'unsettled' && entry.rq_id === null) {
      context.addIssue({
        code: 'custom',
        path: ['rq_id'],
        message: 'a research question nobody asked or answered is named',
      });
    }
  });

const cardSchema = z.strictObject({
  rq_id: z.uuid(),
  rq_text: storedText,
  status: z.enum(['answered', 'not_settled']),
  headline: richTextSchema,
  detail: richTextSchema,
  visual: z.discriminatedUnion('kind', [pollVisualSchema, themesVisualSchema]).nullable(),
  quotes: z.array(briefQuoteSchema).max(BRIEF_CARD_QUOTE_LIMIT),
});

const methodSchema = z.strictObject({
  completes: count,
  ordered_n: count,
  format: z.strictObject({ open: count, polls: count }),
  /** About how long the conversation took each person, in whole minutes. */
  minutes: count,
  fielded: z.strictObject({
    launched_at: z.iso.datetime().nullable(),
    ended_at: z.iso.datetime().nullable(),
  }),
  audience: z.strictObject({
    description: storedText,
    countries: z.array(z.enum(COUNTRIES)).min(1),
  }),
  /** The accepted audience's Limitations, word for word and in their stored order. */
  limitations: z.strictObject({
    targeting_gaps: z.array(z.strictObject({ id: z.uuid(), text: storedText })),
    pipeline_constraints: z.array(
      z.strictObject({ key: z.enum(PIPELINE_CONSTRAINT_KEYS), text: storedText }),
    ),
  }),
  versions: z.strictObject({
    rqs: z.int().positive(),
    dg: z.int().positive(),
    audience: z.int().positive(),
    goal: z.int().positive(),
  }),
  /** Whether the participants were generated rather than recruited. */
  simulated: z.boolean(),
});

const appendixSchema = z.strictObject({
  /** The purchased guide, word for word. */
  guide: z.array(
    z.strictObject({
      title: storedText,
      questions: z.array(
        z.strictObject({
          id: z.uuid(),
          type: z.enum(QUESTION_TYPES),
          text: storedText,
          options: z.array(z.strictObject({ id: z.uuid(), text: storedText })),
        }),
      ),
    }),
  ),
  /** Every served poll's counts, each once. */
  poll_tables: z.array(pollVisualSchema.omit({ kind: true })).max(MAX_STORED_ITEMS),
  /** Every written answer to every served question, by participant. */
  answers: z.array(
    z.strictObject({
      dg_question_id: z.uuid(),
      responses: z.array(
        z.strictObject({ participant: z.string().min(1), text: z.string().min(1) }),
      ),
    }),
  ),
  /**
   * Always empty. A Brief's follow-up log is read from its follow-ups' outcomes, which are kept
   * apart from its versions, so no version holds one.
   */
  follow_up_log: z.array(z.never()),
});

const verdictSchema = z
  .strictObject({
    /** The Study Type's name, or Custom and the Category it was started from. */
    eyebrow: storedText.nullable(),
    headline: richTextSchema,
    /**
     * The headline Sigora wrote, when the researcher's wording replaced it; `null` while the
     * headline is Sigora's own. The researcher rewords around the counts, never them.
     */
    sigora_headline: richTextSchema.nullable(),
    sub_verdict: richTextSchema,
    /** The decision the chosen Goal informs. */
    informs: storedText,
    confidence: z.strictObject({ tier: z.enum(CONFIDENCE_TIERS), completes: count }),
  })
  .superRefine((verdict, context) => {
    if (verdict.sigora_headline === null) {
      return;
    }
    if (sameRichText(verdict.sigora_headline, verdict.headline)) {
      context.addIssue({
        code: 'custom',
        path: ['sigora_headline'],
        message: "a headline in Sigora's own words keeps no other",
      });
    }
    if (!sameRichText(richTextChips(verdict.sigora_headline), richTextChips(verdict.headline))) {
      context.addIssue({
        code: 'custom',
        path: ['headline'],
        message: "the researcher's headline holds Sigora's counts, every one and in order",
      });
    }
  });

const recommendationSchema = z
  .strictObject({
    recommendations: z.array(listItem('R')).min(1).max(MAX_BRIEF_LIST_ITEMS),
    /**
     * The order Sigora wrote the recommendations in, by id, when the researcher's order replaced
     * it; `null` while the order is Sigora's own.
     */
    sigora_order: z.array(itemId('R')).nullable(),
    not_settled: z.array(notSettledSchema).max(MAX_BRIEF_ENTRIES),
  })
  .superRefine((recommendation, context) => {
    const ids = recommendation.recommendations.map((item) => item.id);
    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: 'custom',
        path: ['recommendations'],
        message: 'each recommendation has its own id',
      });
    }
    const order = recommendation.sigora_order;
    if (order === null) {
      return;
    }
    if (!isPermutationOf(order, ids)) {
      context.addIssue({
        code: 'custom',
        path: ['sigora_order'],
        message: "Sigora's order names every recommendation, each once",
      });
    } else if (order.every((id, index) => id === ids[index])) {
      context.addIssue({
        code: 'custom',
        path: ['sigora_order'],
        message: "recommendations in Sigora's own order keep no other",
      });
    }
  });

const summarySchema = z
  .array(summaryItem)
  .min(1)
  .max(MAX_BRIEF_LIST_ITEMS)
  .superRefine((summary, context) => {
    if (new Set(summary.map((item) => item.id)).size !== summary.length) {
      context.addIssue({ code: 'custom', message: 'each summary bullet has its own id' });
    }
    if (!summary.some((item) => item.visible)) {
      context.addIssue({ code: 'custom', message: 'at least one summary bullet shows' });
    }
  });

/**
 * Version 2 of the Brief: the blocks as Sigora composed them, with the researcher's framing in
 * them — which summary bullets show, the Verdict's headline in their words, and the order of the
 * recommendations — and, beside each reworded or reordered field, what Sigora wrote there. So one
 * version is a whole snapshot: it renders, marks what the researcher changed, and says what Sigora
 * wrote underneath, without another version. Strict throughout, so a key this shape does not name
 * is refused rather than dropped from a version that can never be rewritten.
 */
export const briefContentSchemaV2 = z.strictObject({
  verdict: verdictSchema,
  summary: summarySchema,
  recommendation: recommendationSchema,
  evidence: z.strictObject({ cards: z.array(cardSchema).min(1).max(MAX_BRIEF_ENTRIES) }),
  method: methodSchema,
  appendix: appendixSchema,
  /** Where the evidence the Brief was written from came from, and when it was assembled. */
  evidence_meta: z.strictObject({
    source: z.enum(EVIDENCE_SOURCES),
    schema_version: z.int().positive(),
    assembled_at: z.iso.datetime(),
  }),
});

/** The Brief's content as it is written now. */
export const briefContentSchema = briefContentSchemaV2;
export type BriefContent = z.infer<typeof briefContentSchemaV2>;
export type BriefCard = BriefContent['evidence']['cards'][number];
export type BriefNotSettled = BriefContent['recommendation']['not_settled'][number];
export type BriefMethod = BriefContent['method'];
export type BriefAppendix = BriefContent['appendix'];

/**
 * The queue a Brief is written on. Retrying, with a dead letter: the job settles every outcome a
 * model or the evidence gives, and throws only when something unexpected stops it, so a worker that
 * died part-way is redelivered and a request is never left waiting on a job that is gone.
 */
export const QUEUE_BRIEF_SYNTHESIZE = 'brief.synthesize';

/** Where a synthesis job lands once its retries are spent; its handler settles the request. */
export const QUEUE_BRIEF_SYNTHESIZE_DEAD_LETTER = 'brief.synthesize.dead-letter';

/** The job: the study and the request it answers, so a job for a replaced request does nothing. */
export const briefSynthesisPayloadSchema = z.strictObject({
  studyId: z.uuid(),
  requestId: z.uuid(),
});
export type BriefSynthesisPayload = z.infer<typeof briefSynthesisPayloadSchema>;

/**
 * Where a request to write a study's Brief stands.
 *
 * - `pending` — the job has not finished.
 * - `published` — the Brief is written, and the study complete.
 * - `unavailable` — it could not be written, for a reason that is not the evidence's: try again.
 * - `unreadable` — the synthesis answered with something that is not a Brief: try again.
 * - `declined` — too few people completed for a Brief to be written; nothing to try again.
 */
export const BRIEF_SYNTHESIS_STATUSES = [
  'pending',
  'published',
  'unavailable',
  'unreadable',
  'declined',
] as const;
export type BriefSynthesisStatus = (typeof BRIEF_SYNTHESIS_STATUSES)[number];

/**
 * Why the evidence for a Brief could not be had: `EVIDENCE_UNAVAILABLE_REASONS`, restated here
 * because this module reads no value from `brief-evidence.ts`, held to it both ways by type —
 * nothing it adds (`satisfies`), nothing it lacks (`EvidenceReasonsRestated`) — and to its order by
 * test.
 */
const EVIDENCE_UNAVAILABLE_REASONS = [
  'evidence_unavailable',
  'evidence_outage',
  'analysis_not_ready',
  'evidence_refused',
  'evidence_mismatch',
  'evidence_unreadable',
] as const satisfies readonly EvidenceUnavailableReason[];

type HoldsTrue<Check extends true> = Check;

/** Fails to compile when the restated list leaves out a reason `brief-evidence.ts` names. */
export type EvidenceReasonsRestated = HoldsTrue<
  [Exclude<EvidenceUnavailableReason, (typeof EVIDENCE_UNAVAILABLE_REASONS)[number]>] extends [
    never,
  ]
    ? true
    : false
>;

/**
 * Why a Brief could not be written, each open to Try again: no evidence — no assembler for its
 * source, Remesh out of reach, an analysis not ready, a refusal, fieldwork that is not the study's,
 * an answer that could not be read — evidence that broke its contract, no synthesizer or model, a
 * spent budget, a synthesis out of time, a job that died, or accepted versions that are no longer
 * the ones the request was asked over — which Try again asks over afresh. Below the Quality floor is
 * the one reason a request is `declined` instead.
 */
export const BRIEF_SYNTHESIS_UNAVAILABLE_REASONS = [
  ...EVIDENCE_UNAVAILABLE_REASONS,
  'evidence_invalid',
  'synthesizer_unavailable',
  'model_unavailable',
  'budget_spent',
  'timed_out',
  'interrupted',
  'inputs_moved',
] as const;
export const BRIEF_SYNTHESIS_REASONS = [
  ...BRIEF_SYNTHESIS_UNAVAILABLE_REASONS,
  'below_quality_floor',
] as const;
export type BriefSynthesisReason = (typeof BRIEF_SYNTHESIS_REASONS)[number];

/**
 * What a request was asked over: the accepted versions, where its evidence comes from, and what
 * writes it. A fixture's evidence is fixed by its completes, scenario and seed, so a retried request
 * reads the same bundle.
 */
export const briefSynthesisInputsSchema = z.strictObject({
  rqs_version: z.int().positive(),
  dg_version: z.int().positive(),
  audience_version: z.int().positive(),
  goal_version: z.int().positive(),
  evidence: z.discriminatedUnion('source', [
    z.strictObject({
      source: z.literal('fixture'),
      completes: count,
      scenario: z.enum(FIXTURE_SCENARIOS),
      seed: z.int(),
    }),
    z.strictObject({ source: z.literal('remesh') }),
  ]),
  synthesizer: z.enum(['model', 'fixture']),
});
export type BriefSynthesisInputs = z.infer<typeof briefSynthesisInputsSchema>;

export const briefSynthesisRequestSchema = z
  .strictObject({
    id: z.uuid(),
    status: z.enum(BRIEF_SYNTHESIS_STATUSES),
    requested_at: z.iso.datetime(),
    settled_at: z.iso.datetime().nullable(),
    attempt: z.int().positive(),
    inputs: briefSynthesisInputsSchema,
    reason: z.enum(BRIEF_SYNTHESIS_REASONS).nullable(),
    published_version: z.int().positive().nullable(),
  })
  .superRefine((request, context) => {
    const issue = (message: string) => {
      context.addIssue({ code: 'custom', message });
    };
    if ((request.status === 'pending') !== (request.settled_at === null)) {
      issue('a request is settled exactly when it is no longer pending');
    }
    if ((request.status === 'published') !== (request.published_version !== null)) {
      issue('a published version is held exactly when the request is published');
    }
    const unavailable = (BRIEF_SYNTHESIS_UNAVAILABLE_REASONS as readonly string[]).includes(
      request.reason ?? '',
    );
    if ((request.status === 'unavailable') !== unavailable) {
      issue('an unavailable request says why, with a reason that can be tried again');
    }
    if ((request.status === 'declined') !== (request.reason === 'below_quality_floor')) {
      issue('a declined request is below the Quality floor');
    }
  });
export type BriefSynthesisRequest = z.infer<typeof briefSynthesisRequestSchema>;

export const BRIEF_SYNTHESIS_SCHEMA_VERSION = 1;

/** What `studies.brief_synthesis` holds: the latest request to write the study's Brief, or none. */
export const storedBriefSynthesisSchema = z.strictObject({
  schema_version: z.literal(BRIEF_SYNTHESIS_SCHEMA_VERSION),
  request: briefSynthesisRequestSchema.nullable(),
});
export type StoredBriefSynthesis = z.infer<typeof storedBriefSynthesisSchema>;

/**
 * The stored request, or nothing. A shape this build cannot read is treated as no request: the
 * study's next request replaces it.
 */
export const parseStoredBriefSynthesis = (stored: unknown): StoredBriefSynthesis | null => {
  if (stored === null || stored === undefined) {
    return null;
  }

  const parsed = storedBriefSynthesisSchema.safeParse(stored);

  return parsed.success ? parsed.data : null;
};

/** The request as the Fielding step reads it, and whether "Try again" may be offered. */
export interface BriefSynthesisView {
  status: BriefSynthesisStatus;
  reason: BriefSynthesisReason | null;
  requestedAt: string;
  settledAt: string | null;
  attempt: number;
  retryable: boolean;
}

/**
 * Whether Try again is offered: over a request that could not be written or read, and over one
 * still `pending` whose job the queue no longer holds — which only the caller, reading the queue,
 * can say (`jobLost`); nothing will ever answer such a request on its own.
 */
export const briefSynthesisRetryable = (
  request: Pick<BriefSynthesisRequest, 'status'>,
  { jobLost = false }: { jobLost?: boolean } = {},
): boolean =>
  request.status === 'unavailable' ||
  request.status === 'unreadable' ||
  (request.status === 'pending' && jobLost);

export const briefSynthesisView = (
  stored: StoredBriefSynthesis | null,
  { jobLost = false }: { jobLost?: boolean } = {},
): BriefSynthesisView | null => {
  const request = stored?.request ?? null;
  if (request === null) {
    return null;
  }

  return {
    status: request.status,
    reason: request.reason,
    requestedAt: request.requested_at,
    settledAt: request.settled_at,
    attempt: request.attempt,
    retryable: briefSynthesisRetryable(request, { jobLost }),
  };
};

/** Why a request could not be written, each open to Try again. */
export type BriefSynthesisUnavailableReason = (typeof BRIEF_SYNTHESIS_UNAVAILABLE_REASONS)[number];

/**
 * A published Brief as it is read: its current version, who wrote it and when, its content, every
 * version's history line (`briefHistoryOf`, oldest first, the current one last), Sigora's last
 * revision, and what its follow-ups added to it as it stands now (`record`: the notes under its
 * cards, the Appendix log and the follow-up count), all from one snapshot.
 */
export interface PublishedBrief {
  version: number;
  createdAt: string;
  authoredBy: AuthorKind;
  content: BriefContent;
  history: BriefHistoryEntry[];
  lastRevision: BriefRevision | null;
  record: FollowUpRecord;
}

/** One version of a published Brief, current or not, with its history line and its content. */
export interface BriefVersionRead {
  version: number;
  authoredBy: AuthorKind;
  reason: ArtifactVersionReason | null;
  createdAt: string;
  notes: BriefChangeNotes | null;
  content: BriefContent;
}

/**
 * What `GET /studies/:id/brief` answers: the study's state, its latest request to write the Brief,
 * and the Brief itself once the study is complete — `null` before then, whatever is stored.
 */
export interface BriefRead {
  state: StudyState;
  synthesis: BriefSynthesisView | null;
  brief: PublishedBrief | null;
}
